import Grid from '@mui/material/Grid'
import { queryClient } from 'helpers/queryClient'
import { systemIsDigitalTwinSystem } from 'helpers/system.helper/system.helper'
import moment from 'moment'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { Card } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { registerDefaultProps, registerUiConfigComponent } from 'ui/uiConfig/factory'
import UiConfigComponent from 'ui/uiConfig/UiConfigComponent'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import PeriodPicker from 'views/UnitAvailabilityView/components/UnitAvailabilityWidget/components/PeriodPicker/PeriodPicker'
import PlannedStopSettings from 'views/UnitAvailabilityView/components/UnitAvailabilityWidget/components/PlannedStopModal/PlannedStopSettings/PlannedStopSettings'
import DeviationSettings from '../FollowupProduction/DeviationSettings/DeviationSettings'
import styles from './UnitAvailability.module.less'
import type { UnitAvailabilityProps, UnitAvailabilityUnitWidget } from './UnitAvailability.types'

export const UNIT_AVAILABILITY_QUERY_KEY = 'unitAvailabilityQueryKey'

const defaultProps: Omit<UnitAvailabilityProps, 'datasets' | 'children' | 'systemId'> = {
  unitAvailabilityUnits: [
    {
      unit_name: '',
      unit_display_name: '',
      kpi: {
        ui_config_id: NaN,
      },
      chart: {
        ui_config_id: NaN,
      },
    },
  ],
}
registerUiConfigComponent('unit_availability', UnitAvailability, {
  isSingletonComponent: true,
  title: 'Tillgänglighet',
})
registerDefaultProps('unit_availability', defaultProps)

type AvailabilityWidgetProps = {
  unitWidget: UnitAvailabilityUnitWidget
}

function UnitAvailabilityWidget({ unitWidget }: AvailabilityWidgetProps): ReactElement {
  const { activeSystem } = useAuth()
  let unitDisplayName = unitWidget.unit_display_name ?? unitWidget.unit_name

  if (activeSystem && systemIsDigitalTwinSystem(activeSystem)) {
    const primaryDigitalTwin = activeSystem?.primary_digital_twin
    unitDisplayName = getPrettyName(
      unitWidget.unit_display_name ?? unitWidget.unit_name,
      primaryDigitalTwin.translations
    )
  }
  const [period, setPeriod] = useState({ startTime: moment().startOf('month'), endTime: moment().endOf('month') })
  const unitName = unitWidget.unit_name

  const uiConfigSnap = useSnapshot(uiConfigStore)
  const { t } = useTranslation()

  function handleOnChange(): void {
    queryClient.invalidateQueries({ queryKey: [UNIT_AVAILABILITY_QUERY_KEY] })
  }

  return (
    <div className={styles.UnitAvailability_Widget}>
      {
        <Grid size={{ xs: 12 }}>
          <Card tourId={'AvailabilityWidget'} title={`${t('Availability')} - ${unitDisplayName}`}>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
              style={{ overflow: 'hidden' }}
            >
              <Grid
                container
                direction="column"
                size={{ xs: 4 }}
                spacing={4}
                alignItems="center"
                justifyContent="space-between"
              >
                <PeriodPicker setPeriod={setPeriod} />
                <UiConfigComponent
                  datasetRefreshToken={UNIT_AVAILABILITY_QUERY_KEY}
                  systemId={activeSystem?.id ?? 0}
                  id={unitWidget.kpi.ui_config_id}
                  datasetStartTime={Datetime.toISOString(period.startTime)}
                  datasetEndTime={Datetime.toISOString(period.endTime)}
                  uid={uiConfigSnap.idToUiConfig[unitWidget.kpi.ui_config_id]?.uid}
                />
                <Grid>
                  <DeviationSettings
                    startTime={period.startTime.toISOString() as ISODateTime}
                    endTime={period.endTime.toISOString() as ISODateTime}
                    specificUnitAvailability={unitName}
                  />
                </Grid>
                <PlannedStopSettings period={period} unitName={unitName} onRefetch={handleOnChange} />
              </Grid>
              <Grid size={8}>
                <UiConfigComponent
                  datasetRefreshToken={UNIT_AVAILABILITY_QUERY_KEY}
                  systemId={activeSystem?.id ?? 0}
                  id={unitWidget.chart.ui_config_id}
                  datasetStartTime={Datetime.toISOString(period.startTime)}
                  datasetEndTime={Datetime.toISOString(period.endTime)}
                  uid={uiConfigSnap.idToUiConfig[unitWidget.chart.ui_config_id]?.uid}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
      }
    </div>
  )
}

export default function UnitAvailability({ unitAvailabilityUnits }: UnitAvailabilityProps): ReactElement {
  return (
    <>
      {unitAvailabilityUnits.map((unitWidget, index) => {
        return <UnitAvailabilityWidget key={index} unitWidget={unitWidget} />
      })}
    </>
  )
}
